/**
 * Admin API Client
 * Centralized axios client with auto-injected Authorization Bearer token
 */
import axios from "axios";
import { getStoredToken } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const adminAxios = axios.create({
  baseURL: `${API}/admin`,
  timeout: 60000,
});
export { adminAxios };

// Auto-inject Bearer token + handle 401 globally
adminAxios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminAxios.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.status === 401) {
      // Auto-logout on token expiry
      localStorage.removeItem("vupp_admin_token");
      if (window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

// === Auth ===
export const adminLogin = (username, password, turnstileToken) =>
  axios
    .post(`${API}/admin/auth/login`, { username, password, turnstile_token: turnstileToken })
    .then((r) => r.data);
export const adminMe = () => adminAxios.get("/auth/me").then((r) => r.data);
export const adminLogout = () => adminAxios.post("/auth/logout").then((r) => r.data);
export const changeMyPassword = (newPassword) =>
  adminAxios.put("/auth/change-my-password", { new_password: newPassword }).then((r) => r.data);

// === Dashboard Messages ===
export const listMessages = () => adminAxios.get("/messages").then((r) => r.data);
export const createMessage = (data) => adminAxios.post("/messages", data).then((r) => r.data);
export const updateMessage = (id, data) => adminAxios.put(`/messages/${id}`, data).then((r) => r.data);
export const deleteMessage = (id) => adminAxios.delete(`/messages/${id}`).then((r) => r.data);

// === Users (admin only) ===
export const listUsers = () => adminAxios.get("/users").then((r) => r.data);
export const createUser = (data) => adminAxios.post("/users", data).then((r) => r.data);
export const updateUser = (userId, data) =>
  adminAxios.put(`/users/${userId}`, data).then((r) => r.data);
export const changeUserPassword = (userId, newPassword) =>
  adminAxios.put(`/users/${userId}/password`, { new_password: newPassword }).then((r) => r.data);
export const deleteUser = (userId) => adminAxios.delete(`/users/${userId}`).then((r) => r.data);

// Trial claims (Clients section)
export const listTrials = () => adminAxios.get("/trials").then((r) => r.data);
export const deleteTrial = (mac) =>
  adminAxios.delete(`/trials/${encodeURIComponent(mac)}`).then((r) => r.data);

// Website config (store URLs, direct download links, legal URLs shown on Landing page)
export const getWebsiteConfig = () =>
  adminAxios.get("/website-config").then((r) => r.data);
export const updateWebsiteConfig = (data) =>
  adminAxios.put("/website-config", data).then((r) => r.data);

// Paygate config (payment gateway credentials shown on Activation page)
export const getPaygateConfig = () =>
  adminAxios.get("/paygate-config").then((r) => r.data);
export const updatePaygateConfig = (data) =>
  adminAxios.put("/paygate-config", data).then((r) => r.data);

// Price plans (client plans + reseller credit packs)
export const getPricePlans = () =>
  adminAxios.get("/price-plans").then((r) => r.data);
export const updatePricePlans = (data) =>
  adminAxios.put("/price-plans", data).then((r) => r.data);

// Credit orders (reseller purchases pending admin approval)
export const listCreditOrders = () =>
  adminAxios.get("/credit-orders").then((r) => r.data);
export const approveCreditOrder = (orderId) =>
  adminAxios.post(`/credit-orders/${orderId}/approve`).then((r) => r.data);

// Pending payments (PayFast + future gateways; audit + force-settle)
export const listPendingPayments = () =>
  adminAxios
    .get("/paygate/payfast/pending", { baseURL: adminAxios.defaults.baseURL.replace(/\/admin$/, "") })
    .then((r) => r.data);
export const forceSettlePayment = (paymentId) =>
  adminAxios
    .post(
      `/paygate/payfast/pending/${encodeURIComponent(paymentId)}/force-settle`,
      {},
      { baseURL: adminAxios.defaults.baseURL.replace(/\/admin$/, "") },
    )
    .then((r) => r.data);

// Reseller self-service: pull my credit packs + create purchase intent
export const getResellerCreditPacks = () =>
  adminAxios.get("/reseller/credit-packs", { baseURL: adminAxios.defaults.baseURL.replace(/\/admin$/, "") }).then((r) => r.data);
export const createResellerPurchaseIntent = (packId, gateway) =>
  adminAxios
    .post(
      "/reseller/purchase-intent",
      { pack_id: packId, gateway },
      { baseURL: adminAxios.defaults.baseURL.replace(/\/admin$/, "") },
    )
    .then((r) => r.data);

// Credits
export const addCredits = (userId, amount, note) =>
  adminAxios
    .post(`/users/${userId}/credits/add`, { amount, note })
    .then((r) => r.data);
export const setCredits = (userId, amount, note) =>
  adminAxios
    .put(`/users/${userId}/credits/set`, { amount, note })
    .then((r) => r.data);
export const toggleUnlimitedCredits = (userId, unlimited) =>
  adminAxios
    .put(`/users/${userId}/credits/unlimited`, null, { params: { unlimited } })
    .then((r) => r.data);
export const listCreditLogs = (userId) =>
  adminAxios.get(`/users/${userId}/credit-logs`).then((r) => r.data);

// Impersonation
export const impersonateUser = (userId) =>
  adminAxios.post(`/users/${userId}/impersonate`).then((r) => r.data);

// === Settings (admin only for write, all auth for read) ===
export const getSettings = () => adminAxios.get("/settings").then((r) => r.data);
export const updateSettings = (data) =>
  adminAxios.put("/settings", data).then((r) => r.data);

// === Backup / Restore (admin only) ===
// Note: adminAxios has baseURL `/api/admin`, so paths here MUST omit
// the `/admin/` prefix or the URL becomes `/api/admin/admin/...` (404).
// Returns the full snapshot as a JSON object the caller writes to disk.
export const getAdminBackup = () =>
  adminAxios.get("/backup").then((r) => r.data);
// Sends a previously-downloaded backup back to the server. `wipe_first`
// controls whether the restore is destructive.
export const postAdminRestore = (payload) =>
  adminAxios.post("/restore", payload).then((r) => r.data);

// Server-side rolling backup snapshots — saved on the server's disk +
// indexed in `backups_col`. Different from getAdminBackup() which
// streams a one-off JSON download to the admin's machine.
export const createServerBackup = () =>
  adminAxios.post("/backups/create").then((r) => r.data);
export const listServerBackups = () =>
  adminAxios.get("/backups").then((r) => r.data);
export const deleteServerBackup = (id) =>
  adminAxios.delete(`/backups/${id}`).then((r) => r.data);
// Grab the blob payload + let the caller turn it into a download —
// adminAxios automatically attaches the Bearer token (a plain `<a>`
// can't do that).
export const downloadServerBackup = (id) =>
  adminAxios
    .get(`/backups/${id}/download`, { responseType: "blob" })
    .then((r) => r.data);

// === Public ===
export const getPublicPanelInfo = () =>
  axios.get(`${API}/public/panel-info`).then((r) => r.data);

// === Cover proxy (admin) ===
// Server-side Plex cover cache — see `/app/backend/cover_proxy.py`.
// Stats card lives at Admin → Playlists (one card per playlist row).
export const getCoverStats = () =>
  adminAxios.get("/covers/stats").then((r) => r.data);
export const refreshCovers = (playlistId, refreshAll = false) =>
  adminAxios
    .post(`/covers/refresh/${playlistId}${refreshAll ? "?refresh_all=true" : ""}`)
    .then((r) => r.data);
export const listCovers = (playlistId, opts = {}) => {
  const { status = "all", limit = 100, offset = 0 } = opts;
  return adminAxios
    .get(`/covers/list/${playlistId}`, { params: { status, limit, offset } })
    .then((r) => r.data);
};
export const retryFailedCovers = (playlistId) =>
  adminAxios.post(`/covers/retry-failed/${playlistId}`).then((r) => r.data);
export const deleteCorruptedCovers = (playlistId) =>
  adminAxios
    .post(`/covers/delete-corrupted/${playlistId}`)
    .then((r) => r.data);
export const deleteFailedCovers = (playlistId) =>
  adminAxios.post(`/covers/delete-failed/${playlistId}`).then((r) => r.data);
export const deleteCover = (hash) =>
  adminAxios.delete(`/covers/${hash}`).then((r) => r.data);
export const testCoverUrl = (url) =>
  adminAxios.post(`/covers/test`, { url }).then((r) => r.data);

// === Dashboard ===
export const getDashboardStats = () => adminAxios.get("/dashboard/stats").then((r) => r.data);

// === Keys ===
export const generateKey = (note, opts = {}) =>
  adminAxios
    .post("/keys/generate", { note, is_trial: !!opts.is_trial })
    .then((r) => r.data);
export const listKeys = () => adminAxios.get("/keys").then((r) => r.data);
export const getKeyDetails = (keyId) => adminAxios.get(`/keys/${keyId}`).then((r) => r.data);
export const assignPlaylistsToKey = (keyId, playlistIds) =>
  adminAxios
    .post(`/keys/${keyId}/assign-playlists`, { playlist_ids: playlistIds })
    .then((r) => r.data);
export const deleteKey = (keyId) => adminAxios.delete(`/keys/${keyId}`).then((r) => r.data);
export const updateKeyStatus = (keyId, newStatus) =>
  adminAxios
    .put(`/keys/${keyId}/status`, null, { params: { new_status: newStatus } })
    .then((r) => r.data);
export const convertTrialToPaid = (keyId) =>
  adminAxios.post(`/keys/${keyId}/convert-to-paid`).then((r) => r.data);

export const renewKey = (keyId) =>
  adminAxios.post(`/keys/${keyId}/renew`).then((r) => r.data);

// v1.4.38 — Connect / disconnect MAC to key. Rebinds an existing
// device with this MAC to the key (transferring the client to the
// key-owner's reseller). Rejects (409) if the MAC belongs to a
// different reseller.
export const connectMacToKey = (keyId, macAddress) =>
  adminAxios.put(`/keys/${keyId}/connect-mac`, { mac_address: macAddress }).then((r) => r.data);
export const disconnectMacFromKey = (keyId) =>
  adminAxios.put(`/keys/${keyId}/disconnect-mac`).then((r) => r.data);

// v1.3.41 — per-key / per-playlist Debug-mode toggles. The player
// reads the effective state (OR of key + assigned playlists) from
// /api/player/playlists on every refresh and auto-toggles its
// in-app debug console accordingly.
export const setKeyDebugMode = (keyId, enabled) =>
  adminAxios.put(`/keys/${keyId}/debug-mode`, { enabled: !!enabled }).then((r) => r.data);

export const setPlaylistDebugMode = (playlistId, enabled) =>
  adminAxios.put(`/playlists/${playlistId}/debug-mode`, { enabled: !!enabled }).then((r) => r.data);

// v1.3.44 — server-side Cover & Metadata Harvest controls.
export const setPlaylistB2cEnabled = (playlistId, enabled) =>
  adminAxios.put(`/playlists/${playlistId}/b2c-toggle`, { enabled: !!enabled }).then((r) => r.data);

export const getPlaylistHarvestStatus = (playlistId) =>
  adminAxios.get(`/playlists/${playlistId}/harvest-status`).then((r) => r.data);

export const runPlaylistHarvest = (playlistId) =>
  adminAxios.post(`/playlists/${playlistId}/run-harvest`).then((r) => r.data);

export const deletePlaylistSnapshot = (playlistId) =>
  adminAxios.delete(`/playlists/${playlistId}/snapshot`).then((r) => r.data);

// v1.3.48 — Server-side M3U pre-parse snapshot controls.
export const setPlaylistSnapshotEnabled = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/playlist-snapshot-toggle`, { enabled: !!enabled })
    .then((r) => r.data);

export const getPlaylistSnapshotStatus = (playlistId) =>
  adminAxios
    .get(`/playlists/${playlistId}/playlist-snapshot-status`)
    .then((r) => r.data);

export const runPlaylistSnapshot = (playlistId) =>
  adminAxios
    .post(`/playlists/${playlistId}/run-playlist-snapshot`)
    .then((r) => r.data);

// v1.3.58 — three new per-playlist toggles for the dedicated
// Cover & Snapshot page.
export const setPlaylistB2cAutoHarvest = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/b2c-auto-toggle`, { enabled: !!enabled })
    .then((r) => r.data);

export const setPlaylistSnapshotAutoRun = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/playlist-snapshot-auto-toggle`, {
      enabled: !!enabled,
    })
    .then((r) => r.data);

export const setSnapshotRequiredOnStart = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/snapshot-required-on-start-toggle`, {
      enabled: !!enabled,
    })
    .then((r) => r.data);

// v1.4.26 — Per-playlist "Disable series pre-loading" toggle. See
// backend `admin_playlists.py` route + the frontend series-harvest
// gate in `xtreamApi.js` (`enrichChannelsWithXtream`, opts.skipSeriesHarvest).
export const setDisableSeriesPreload = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/disable-series-preload-toggle`, {
      enabled: !!enabled,
    })
    .then((r) => r.data);

// v1.4.27 — Per-playlist "Prefer M3U source (legacy)" opt-out. When
// enabled the client reverts to the legacy M3U-first flow instead of
// the new Xtream-API-first fast path.
export const setPreferM3uSource = (playlistId, enabled) =>
  adminAxios
    .put(`/playlists/${playlistId}/prefer-m3u-source-toggle`, {
      enabled: !!enabled,
    })
    .then((r) => r.data);

export const deletePlaylistPreparsedSnapshot = (playlistId) =>
  adminAxios
    .delete(`/playlists/${playlistId}/playlist-snapshot`)
    .then((r) => r.data);

// v1.3.70 — Xtream pre-warm bundle controls (admin UI for the
// `playlist_prewarm.py` harvester). The bundle is a single gzipped
// JSON blob the player downloads on cold start to hydrate IDB in
// one round trip instead of N per-category fetches.
export const getPlaylistPrewarmStatus = (playlistId) =>
  adminAxios
    .get(`/playlists/${playlistId}/xtream-prewarm/status`)
    .then((r) => r.data);

export const runPlaylistPrewarm = (playlistId) =>
  adminAxios
    .post(`/playlists/${playlistId}/xtream-prewarm`)
    .then((r) => r.data);

export const deletePlaylistPrewarm = (playlistId) =>
  adminAxios
    .delete(`/playlists/${playlistId}/xtream-prewarm`)
    .then((r) => r.data);

// === Playlists ===
export const createPlaylist = (data) => adminAxios.post("/playlists", data).then((r) => r.data);
export const listPlaylists = () => adminAxios.get("/playlists").then((r) => r.data);
export const getPlaylistDetails = (id) => adminAxios.get(`/playlists/${id}`).then((r) => r.data);
export const updatePlaylist = (id, data, playlistPassword) => {
  // v1.4.38 — Resellers with a "Playlist Lock" must pass the password
  // via the X-Playlist-Password header. Admins can omit it.
  const headers = playlistPassword ? { "X-Playlist-Password": playlistPassword } : undefined;
  return adminAxios
    .put(`/playlists/${id}`, data, headers ? { headers } : undefined)
    .then((r) => r.data);
};
export const unlockPlaylist = (id, password) =>
  adminAxios.post(`/playlists/${id}/unlock`, { password }).then((r) => r.data);
export const setMyPlaylistPassword = (newPassword) =>
  adminAxios.put("/auth/playlist-password", { new_password: newPassword || "" }).then((r) => r.data);
export const deletePlaylist = (id) => adminAxios.delete(`/playlists/${id}`).then((r) => r.data);

// === Devices ===
export const listDevices = (filters = {}) =>
  adminAxios.get("/devices", { params: filters }).then((r) => r.data);
export const getDeviceStats = () => adminAxios.get("/devices/stats").then((r) => r.data);
// v1.3.77 — credential-sharing report
export const getDeviceSharing = () =>
  adminAxios.get("/devices/sharing").then((r) => r.data);
// v1.3.78 — bulk-block every device on a shared key
export const blockAllDevicesOnKey = (activationKey) =>
  adminAxios
    .post(`/devices/sharing/${encodeURIComponent(activationKey)}/block-all`)
    .then((r) => r.data);
export const updateDeviceStatus = (deviceId, newStatus) =>
  adminAxios.put(`/devices/${deviceId}/status`, null, { params: { new_status: newStatus } }).then((r) => r.data);
export const deleteDevice = (deviceId) => adminAxios.delete(`/devices/${deviceId}`).then((r) => r.data);
// v1.3.82 — Bulk-delete a batch of devices in one call. `deviceIds`
// is an array of internal UUIDs (`d.id`, NOT the MAC/UDID string).
// The backend also purges the per-device pre-warm bundles for each
// deleted device.
export const bulkDeleteDevices = (deviceIds) =>
  adminAxios
    .post("/devices/bulk-delete", { device_ids: deviceIds })
    .then((r) => r.data);

// === Branding ===
export const getBranding = () => adminAxios.get("/branding").then((r) => r.data);
export const uploadLogo = (variant, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return adminAxios.post(`/branding/upload-logo`, formData, {
    params: { variant },
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const uploadIntro = (variant, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return adminAxios.post(`/branding/upload-intro`, formData, {
    params: { variant },
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const deleteLogo = (variant) =>
  adminAxios.delete(`/branding/logo/${variant}`).then((r) => r.data);
export const deleteIntro = (variant) =>
  adminAxios.delete(`/branding/intro/${variant}`).then((r) => r.data);
export const updateAppName = (appName) =>
  adminAxios.put(`/branding/app-name`, null, { params: { app_name: appName } }).then((r) => r.data);
export const updateVersion = (data) =>
  adminAxios.put(`/branding/version`, data).then((r) => r.data);

// === Notifications ===
export const sendNotification = (data) =>
  adminAxios.post("/notifications/send", data).then((r) => r.data);
export const listNotifications = () => adminAxios.get("/notifications").then((r) => r.data);
export const deleteNotification = (id) =>
  adminAxios.delete(`/notifications/${id}`).then((r) => r.data);

// === Health / nightly smoke test (admin only) ===
// v1.4.28 — surfaces the state of the supervisor `nightly-smoketest`
// daemon in the Admin panel's App Store Readiness page.
export const getSmokeHealth = () =>
  adminAxios.get("/health/smoke").then((r) => r.data);
// v1.4.28-f — filesystem + `.git` + webpack-cache summary. Fed by
// the `git-maintenance` supervisor daemon.
export const getStorageHealth = () =>
  adminAxios.get("/health/storage").then((r) => r.data);
// v1.4.28-i — one-click "Kick maintenance now" that runs
// `/app/scripts/git-maintenance.sh --once` server-side and returns
// {status, duration_s, free_before_mb, free_after_mb, freed_mb, tail,
// exit_code}. See routes/admin_health.py::kick_storage_maintenance.
export const kickStorageMaintenance = () =>
  adminAxios
    .post("/health/storage/maintenance-run", null, { timeout: 130_000 })
    .then((r) => r.data);

// Helper to get full asset URL
export const getAssetUrl = (relativePath) => {
  if (!relativePath) return null;
  if (relativePath.startsWith("http")) return relativePath;
  return `${BACKEND_URL}${relativePath}`;
};
